/**
 * API client.
 *
 * The admin JWT is kept in localStorage. That is a deliberate, bounded choice
 * for an internal admin console: the token is short-lived (8h), the API is the
 * only thing that accepts it, and the alternative — a cookie — would need CSRF
 * protection across a separate dev origin. No other credential of any kind is
 * ever held in the browser.
 */

const BASE = import.meta.env.VITE_API_BASE_URL || '';
const TOKEN_KEY = 'tab.admin.token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private browsing; the session simply will not persist */
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Multipart body; Content-Type is left to the browser to set the boundary. */
  form?: FormData;
  signal?: AbortSignal;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (!options.form) headers['Content-Type'] = 'application/json';

  const response = await fetch(`${BASE}/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    ...(options.form
      ? { body: options.form }
      : options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    /* some errors have no body */
  }

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: unknown } })
      ?.error;

    // An expired session should return the admin to the login screen rather
    // than showing a confusing error on every panel of the page.
    if (response.status === 401 && getToken()) {
      setToken(null);
      window.location.hash = '#/login';
    }

    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? `Request failed with status ${response.status}.`,
      error?.details,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'DELETE', ...(body !== undefined ? { body } : {}) }),
  upload: <T>(path: string, form: FormData) => apiRequest<T>(path, { method: 'POST', form }),
};

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

export type OnlineState = 'online' | 'offline' | 'never_seen';
export type PolicyStatus = 'none' | 'applied' | 'pending' | 'outdated' | 'partial' | 'failed';

/** The student currently holding a device. `null` means unassigned. */
export interface StudentAssignment {
  studentRef: string | null;
  studentName: string | null;
  assignedAt: string | null;
}

export interface DeviceAlert {
  id: string;
  deviceId: string;
  deviceLabel: string | null;
  studentName: string | null;
  studentRef: string | null;
  alertType: string;
  severity: string;
  previousState: string | null;
  newState: string | null;
  summary: string;
  createdAt: string;
  acknowledgedAt: string | null;
}

export interface Device {
  id: string;
  displayName: string;
  assetTag: string | null;
  /**
   * Who is holding this tablet.
   *
   * `null` is normal - every device enrolled before assignments existed has no
   * student, and the UI shows the device identity instead rather than the word
   * "Unknown", which would imply something had been lost.
   */
  student: StudentAssignment | null;
  enrollmentStatus: string;
  managementState: string;
  online: OnlineState;
  manufacturer: string | null;
  model: string | null;
  androidVersion: string | null;
  androidSdkInt: number | null;
  serialNumber: string | null;
  osBuild: string | null;
  securityPatchLevel: string | null;
  agentVersion: string | null;
  policy: {
    id: string;
    name: string | null;
    version: number | null;
    status: PolicyStatus;
    appliedVersion: number | null;
    appliedAt: string | null;
  } | null;
  unsupportedFeatures: Array<{ key: string; reason: string }>;
  lastSeenAt: string | null;
  enrolledAt: string | null;
  battery: { level: number; charging: boolean | null } | null;
  network: { type: string | null; wifiSsid: string | null };
  kioskActive: boolean | null;
  /** Read back from ActivityManager on the device, not inferred from policy. */
  kioskLockTaskVerified: boolean | null;
  encryptionStatus: string | null;
  policyControlResults: ControlResult[];
  policyOverallStatus: string | null;
  lastPolicyAckAt: string | null;
  enrollmentCount: number;
  lastEnrolledAt: string | null;
  reachability: Reachability;
  storage: { freeBytes: number | null; totalBytes: number | null } | null;
  lastError: { message: string; at: string | null } | null;
  createdAt: string;
}

/** One control, as the device reported it applying. */
export interface ControlResult {
  key: string;
  status: 'APPLIED' | 'FAILED' | 'UNSUPPORTED' | 'SKIPPED';
  detail: string | null;
}

export type OfflineClassification =
  | 'ONLINE'
  | 'POSSIBLY_OFFLINE'
  | 'NETWORK_UNAVAILABLE'
  | 'POWERED_OFF'
  | 'LIKELY_POWERED_OFF'
  | 'MANAGEMENT_AGENT_NOT_REPORTING'
  | 'POSSIBLE_DPC_STOPPED'
  | 'POSSIBLE_UNMANAGED'
  | 'POSSIBLE_FACTORY_RESET'
  | 'POSSIBLE_RE_ENROLLMENT'
  | 'UNKNOWN';

/**
 * The server's reasoned account of why a device is or is not answering.
 * Never a bare "OFFLINE" — always a likelihood with the evidence behind it.
 */
export interface Reachability {
  classification: OfflineClassification;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  headline: string;
  conclusion: string;
  alternatives: string[];
  signals: Array<{ label: string; value: string; supporting: boolean }>;
  secondsSinceLastSeen: number | null;
  missedHeartbeats: number | null;
  expectedIntervalSeconds: number;
  severity: 'info' | 'warning' | 'high' | 'critical';
}

export interface DeviceSummary {
  total: number;
  online: number;
  offline: number;
  neverSeen: number;
  needsAttention: number;
}

export interface Capability {
  key: string;
  label: string;
  status: 'supported' | 'unsupported_android_version' | 'unsupported_by_provider' | 'requires_device_owner';
  minApiLevel: number;
  /** Set when Android WITHDREW the control above a version, e.g. password quality. */
  maxApiLevel?: number;
  androidMechanism: string;
  reason?: string;
  note?: string;
  /** Can destroy data or strand a device; rendered with a warning. */
  destructive?: boolean;
}

export interface Policy {
  id: string;
  name: string;
  description: string;
  kind: 'test' | 'production';
  definition: PolicyDefinition;
  version: number;
  is_default: boolean;
  validated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PolicyDefinition {
  schemaVersion: number;
  kiosk: {
    enabled: boolean;
    kioskPackage?: string;
    replaceHomeLauncher: boolean;
    allowedSystemFeatures: string[];
  };
  applications: {
    allowedPackages: string[];
    blockUninstallManaged: boolean;
    hideUnlistedApps: boolean;
    blockFileManagers: boolean;
    autoGrantPermissions: boolean;
    /** packageName -> SHA-256 hex of the expected signing certificate. */
    expectedSignatures: Record<string, string>;
  };
  screen: {
    statusBarDisabled: boolean;
    keyguardDisabled: boolean;
    screenCaptureDisabled: boolean;
    keepScreenOnWhileCharging: boolean;
  };
  camera: { disabled: boolean };
  password: {
    enabled: boolean;
    quality: 'NONE' | 'SOMETHING' | 'NUMERIC' | 'NUMERIC_COMPLEX' | 'ALPHABETIC' | 'ALPHANUMERIC' | 'COMPLEX';
    minimumLength: number;
    requiredComplexity: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';
    maximumFailedPasswordsForWipe: number;
    expirationDays: number;
    historyLength: number;
  };
  factoryResetProtection: { enabled: boolean; accounts: string[] };
  restrictions: Record<string, boolean>;
  wifi: { networks: WifiNetwork[] };
  checkInIntervalSeconds: number;
}

export interface WifiNetwork {
  ssid: string;
  securityType: 'NONE' | 'WPA' | 'WEP';
  /** A reference to a server-side secret. The password itself never leaves the server. */
  passwordRef?: string;
  hidden: boolean;
  autoConnect: boolean;
}

export interface SafetyIssue {
  severity: 'error' | 'warning';
  field: string;
  message: string;
}

export interface Application {
  id: string;
  package_name: string;
  display_name: string;
  description: string;
  signing_cert_sha256: string | null;
  version_count: number;
  latest_version_code: number | null;
  latest_version_name: string | null;
  latest_uploaded_at: string | null;
  installed_device_count: number;
  created_at: string;
}

export interface ApkVersion {
  id: string;
  version_code: number;
  version_name: string;
  min_sdk_version: number | null;
  target_sdk_version: number | null;
  file_name: string;
  file_size_bytes: number;
  sha256: string;
  signing_cert_sha256: string | null;
  signature_schemes: string[];
  signature_parse_note: string | null;
  permissions: string[];
  notes: string;
  created_at: string;
  uploaded_by_email: string | null;
  deployment_count: number;
}

export type TargetStatus =
  | 'pending'
  | 'awaiting_device'
  | 'notified'
  | 'downloading'
  | 'installing'
  | 'installed'
  | 'failed'
  | 'cancelled';

export interface Deployment {
  id: string;
  status: string;
  target_count: number;
  success_count: number;
  failure_count: number;
  in_flight_count?: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  package_name: string;
  application_name: string;
  version_name: string;
  version_code: number;
  created_by_email: string | null;
}

export interface DeploymentTarget {
  id: string;
  status: TargetStatus;
  status_message: string | null;
  last_error_code: string | null;
  attempts: number;
  notified_at: string | null;
  completed_at: string | null;
  device_id: string;
  device_name: string;
  model: string | null;
  android_version: string | null;
  deviceOnline: OnlineState;
}

export interface AuditLog {
  id: number;
  actor_type: string;
  actor_label: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  success: boolean;
  ip_address: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface ProviderInfo {
  id: string;
  displayName: string;
  active: boolean;
  available: boolean;
  reason: string | null;
  remediation: string | null;
  capabilities: {
    enrollmentMethods: string[];
    supportedCommands: string[];
    delivery: 'push' | 'poll';
    survivesFactoryReset: boolean;
    factoryResetNote: string;
    supportsArbitraryApkInstall: boolean;
  };
}
